import React from 'react';
import { Circle, ArrowDown, Target } from 'lucide-react';

/**
 * Компонент ввода параметров цилиндрической формы
 */
const CylinderSection = ({ formData, handleInputChange, errors }) => {
  // Расчет радиуса из диаметра и наоборот для синхронизации полей
  const updateRelatedField = (field, value) => {
    const numValue = parseFloat(value) || 0;
    
    if (field === 'diameter') {
      // Если изменился диаметр, обновляем радиус
      const radius = (numValue / 2).toFixed(2);
      handleInputChange({ target: { name: 'radius', value: radius } });
    } else if (field === 'radius') {
      // Если изменился радиус, обновляем диаметр
      const diameter = (numValue * 2).toFixed(2);
      handleInputChange({ target: { name: 'diameter', value: diameter } });
    }
  };

  // Обработчик изменений полей с обновлением связанных полей
  const handleCylinderInputChange = (e) => {
    const { name, value } = e.target;
    handleInputChange(e);
    updateRelatedField(name, value);
  };

  return (
    <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
        Размеры цилиндрической конструкции
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Диаметр */}
        <div>
          <label htmlFor="diameter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Circle className="w-4 h-4 mr-1 text-cyan-600" />
            Диаметр (м)
          </label>
          <input
            type="text"
            id="diameter"
            name="diameter"
            value={formData.diameter}
            onChange={handleCylinderInputChange}
            className={`form-input w-full ${errors.diameter ? 'input-error' : ''}`}
            placeholder="Введите диаметр"
          />
          {errors.diameter && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.diameter}
            </p>
          )}
        </div>

        {/* Радиус */}
        <div>
          <label htmlFor="radius" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Target className="w-4 h-4 mr-1 text-cyan-600" />
            Радиус (м)
          </label>
          <input
            type="text"
            id="radius"
            name="radius"
            value={formData.radius}
            onChange={handleCylinderInputChange}
            className={`form-input w-full ${errors.radius ? 'input-error' : ''}`}
            placeholder="Введите радиус"
          />
          {errors.radius && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.radius}
            </p>
          )}
        </div>

        {/* Высота */}
        <div>
          <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowDown className="w-4 h-4 mr-1 text-cyan-600" />
            Высота (м)
          </label>
          <input
            type="text"
            id="height"
            name="height"
            value={formData.height}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.height ? 'input-error' : ''}`}
            placeholder="Введите высоту"
          />
          {errors.height && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.height}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Визуальное представление */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <div className="flex justify-center items-center h-28">
            <div className="relative">
              <div 
                className="rounded-full bg-cyan-100 dark:bg-cyan-800 border-2 border-cyan-400 dark:border-cyan-600" 
                style={{ 
                  width: `${Math.min(120, parseFloat(formData.diameter || 0) * 30)}px`, 
                  height: `${Math.min(120, parseFloat(formData.diameter || 0) * 30)}px` 
                }}
              >
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-cyan-800 dark:text-cyan-200">
                  ∅{formData.diameter} м
                </div>
              </div>
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 dark:text-gray-300">
                h: {formData.height} м
              </div>
            </div>
          </div>
        </div>

        {/* Типовые размеры */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Типовые размеры:</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'diameter', value: '0.4' } });
                handleInputChange({ target: { name: 'radius', value: '0.2' } });
                handleInputChange({ target: { name: 'height', value: '2.5' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Столб ∅0.4×2.5 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'diameter', value: '0.3' } });
                handleInputChange({ target: { name: 'radius', value: '0.15' } });
                handleInputChange({ target: { name: 'height', value: '1.5' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Колонна ∅0.3×1.5 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'diameter', value: '1.5' } });
                handleInputChange({ target: { name: 'radius', value: '0.75' } });
                handleInputChange({ target: { name: 'height', value: '0.2' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Круг. площадка ∅1.5×0.2 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'diameter', value: '2' } });
                handleInputChange({ target: { name: 'radius', value: '1' } });
                handleInputChange({ target: { name: 'height', value: '1.5' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Чаша ∅2×1.5 м
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600">
        <p>Для цилиндрической формы достаточно указать либо диаметр, либо радиус, второе значение будет рассчитано автоматически.</p>
        <p className="mt-1">Объем цилиндра = π × r² × h, где r - радиус, h - высота.</p>
      </div>
    </div>
  );
};

export default CylinderSection;